import React, { useState, useEffect } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react'; // Added Trash2 icon
import Modal, { ModalCloseButton } from './ui/Modal';

const ClinicalFindingsSection = ({
  allclinical_findings,
  setAllClinical_findings,
  pmrn,
  clinicianId,
  API_BASE_URL
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    findingType: '',
    value: '',
    takenAt: '',
    id: null,
  });

  // 🔹 Fetch data
  const fetchClinicalFindings = async () => {
    if (!pmrn) return;
    try {
      const res = await fetch(`${API_BASE_URL}/clinical-findings/patient/${pmrn}`);
      const data = await res.json();
      setAllClinical_findings(data);
    } catch (err) {
      console.error('Failed to fetch clinical findings:', err);
    }
  };

  useEffect(() => {
    fetchClinicalFindings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmrn]);

  // 🔹 Add
  const handleAdd = () => {
    setFormData({
      findingType: '',
      value: '',
      takenAt: '',
      id: null,
    });
    setIsEditing(false);
    setIsModalOpen(true);
  };

  // 🔹 Edit
  const handleEdit = (finding) => {
    setFormData({
      findingType: finding.findingType,
      value: finding.value,
      takenAt: finding.takenAt ? finding.takenAt.slice(0, 10) : '',
      id: finding.id,
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  // 🔹 Delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this clinical finding?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/clinical-findings/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete clinical finding');

      // Refresh data after delete
      fetchClinicalFindings();
    } catch (err) {
      console.error('Error deleting clinical finding:', err);
    }
  };

  // 🔹 Submit (Add / Edit)
  const handleSubmit = async () => {
    const payload = {
      findingType: formData.findingType,
      value: formData.value,
      takenAt: new Date(formData.takenAt).toISOString(),
      PMRN: pmrn,
      enteredByClinicalId: clinicianId,
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/clinical-findings/${isEditing ? `/${formData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error('Request failed');

      setIsModalOpen(false);
      fetchClinicalFindings(); // Refresh table
    } catch (err) {
      console.error('Error saving clinical finding:', err);
    }
  };

  // 🔹 UI
  return (
    <div className="border border-teal-300 rounded p-4 space-y-4">
      <div className="flex justify-between items-center border-b border-teal-300 pb-2">
        <h3 className="font-semibold text-teal-700">Clinical Findings</h3>
        <button
          onClick={handleAdd}
          title="Add"
          className="p-1 rounded hover:bg-teal-100 transition"
          aria-label="Add clinical finding"
        >
          <Plus size={20} color="#0d9488" />
        </button>
      </div>

      {allclinical_findings.length > 0 ? (
        <table className="table-fixed w-full text-sm text-gray-700">
          <thead>
            <tr className="text-left border-b border-gray-300 bg-teal-50">
              <th className="w-1/4 py-2 px-2">Finding Type</th>
              <th className="w-1/4 py-2 px-2">Value</th>
              <th className="w-1/4 py-2 px-2">Date</th>
              <th className="w-1/6 py-2 px-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {allclinical_findings.map((finding) => (
              <tr
                key={finding.id}
                className="border-b hover:bg-gray-50 transition"
              >
                <td className="py-2 px-2">{finding.findingType}</td>
                <td className="py-2 px-2">{finding.value}</td>
                <td className="py-2 px-2">
                  {new Date(finding.takenAt).toLocaleDateString()}
                </td>
                <td className="py-2 px-2 flex justify-center gap-2">
                  <button
                    onClick={() => handleEdit(finding)}
                    className="p-1 rounded hover:bg-teal-100 transition"
                    title="Edit"
                  >
                    <Pencil size={18} color="#0d9488" />
                  </button>
                  <button
                    onClick={() => handleDelete(finding.id)}
                    className="p-1 rounded hover:bg-red-100 transition"
                    title="Delete"
                  >
                    <Trash2 size={18} color="#dc2626" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-gray-500 text-center py-4">
          No patient clinical findings found
        </p>
      )}

      {/* Modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <div className="bg-white p-6 rounded-2xl w-96 space-y-4 shadow-2xl border border-gray-100 relative">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold text-teal-700">
                {isEditing ? 'Edit Clinical Finding' : 'Add Clinical Finding'}
              </h2>
              <ModalCloseButton onClose={() => setIsModalOpen(false)} />
            </div>

            <input
              type="text"
              placeholder="Finding Type"
              className="w-full border p-2 rounded"
              value={formData.findingType}
              onChange={(e) =>
                setFormData({ ...formData, findingType: e.target.value })
              }
            />
            <input
              type="text"
              placeholder="Value"
              className="w-full border p-2 rounded"
              value={formData.value}
              onChange={(e) =>
                setFormData({ ...formData, value: e.target.value })
              }
            />
            <input
              type="date"
              className="w-full border p-2 rounded"
              value={formData.takenAt}
              onChange={(e) =>
                setFormData({ ...formData, takenAt: e.target.value })
              }
            />

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700"
              >
                {isEditing ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
      </Modal>
    </div>
  );
};

export default ClinicalFindingsSection;


// import React, { useState, useEffect } from 'react';

// import { Pencil, Plus } from 'lucide-react';  // import icons

// const ClinicalFindingsSection = ({ allclinical_findings, setAllClinical_findings, pmrn, clinicianId, API_BASE_URL }) => {
  
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [formData, setFormData] = useState({
//     findingType: '',
//     value: '',
//     takenAt: '',
//     id: null,
//   });



//   // Fetch data from backend
//   const fetchClinicalFindings = async () => {
//     if (!pmrn) return;
//     try {
//       const res = await fetch(`${API_BASE_URL}/clinical-findings/patient/${pmrn}`)
//       const data = await res.json();
//       setAllClinical_findings(data);
//     } catch (err) {
//       console.error('Failed to fetch clinical findings:', err);
//     }
//   };

//   useEffect(() => {
//     fetchClinicalFindings();
//   }, [pmrn]);

//     const handleAdd = () => {
//     setFormData({
//       findingType: '',
//       value: '',
//       takenAt: '',
//       id: null,
//     });
//     setIsEditing(false);
//     setIsModalOpen(true);
//   };

//   const handleEdit = (finding) => {
//     setFormData({
//       findingType: finding.findingType,
//       value: finding.value,
//       takenAt: finding.takenAt.slice(0, 10),
//       id: finding.id,
//     });
//     setIsEditing(true);
//     setIsModalOpen(true);
//   };

//   const handleSubmit = async () => {
//     const payload = {
//       findingType: formData.findingType,
//       value: formData.value,
//       takenAt: new Date(formData.takenAt).toISOString(),
//       PMRN: pmrn,
//       enteredByClinicalId: clinicianId, 
//     };

//     try {
//       const response = await fetch(
//         `${API_BASE_URL}/clinical-findings${isEditing ? `/${formData.id}` : ''}`,
//         {
//           method: isEditing ? 'PUT' : 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         }
//       );

//       if (!response.ok) throw new Error('Request failed');

//       setIsModalOpen(false);
//       fetchClinicalFindings(); // Refresh table
//     } catch (err) {
//       console.error('Error saving clinical finding:', err);
//     }
//   };

//   return (
//     <div className="border border-teal-300 rounded p-4 space-y-4">
//       <div className="flex justify-between items-center border-b border-teal-300 pb-2">
//         <h3 className="font-semibold text-teal-700">Clinical Finding</h3>
//         <div>
//           <button
//             onClick={handleAdd}
//             title="Add"
//             className="p-1 rounded hover:bg-teal-100 transition"
//             aria-label="Add clinical finding"
//           >
//             <Plus size={20} color="#0d9488" />
//           </button>
//         </div>
//       </div>

//       {allclinical_findings.length > 0 ? (
//         <table className="table-fixed w-full text-sm text-gray-700">
//           <thead>
//             <tr className="text-left border-b border-gray-300">
//               <th className="w-1/3 py-2">Clinical Type</th>
//               <th className="w-1/3 py-2">Results</th>
//               <th className="w-1/3 py-2">Date</th>
//               <th className="w-16 py-2">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {allclinical_findings.map((finding) => (
//               <tr key={finding.id} className="border-b border-gray-200">
//                 <td className="py-2">{finding.findingType}</td>
//                 <td className="py-2">{finding.value}</td>
//                 <td className="py-2">{new Date(finding.takenAt).toLocaleDateString()}</td>
//                 <td className="py-2">
//                   <button onClick={() => handleEdit(finding)} 
//                     className="p-1 rounded hover:bg-teal-100 transition"
//                     title="Edit"
//                     aria-label={`Edit clinical finding ${finding.findingType}`}
//                   >
//                     <Pencil size={18} color="#0d9488" />
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       ) : (
//         <p className="text-gray-500 text-center py-4">No patient clinical findings found</p>
//       )}

//       {isModalOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg w-96 space-y-4">
//             <h2 className="text-lg font-semibold">{isEditing ? 'Edit' : 'Add'} Clinical Finding</h2>
//             <input
//               type="text"
//               placeholder="Finding Type"
//               className="w-full border p-2"
//               value={formData.findingType}
//               onChange={(e) => setFormData({ ...formData, findingType: e.target.value })}
//             />
//             <input
//               type="text"
//               placeholder="Value"
//               className="w-full border p-2"
//               value={formData.value}
//               onChange={(e) => setFormData({ ...formData, value: e.target.value })}
//             />
//             <input
//               type="date"
//               className="w-full border p-2"
//               value={formData.takenAt}
//               onChange={(e) => setFormData({ ...formData, takenAt: e.target.value })}
//             />
//             <div className="flex justify-end space-x-2">
//               <button onClick={() => setIsModalOpen(false)} className="text-gray-500">Cancel</button>
//               <button onClick={handleSubmit} className="bg-teal-500 text-white px-4 py-2 rounded">
//                 {isEditing ? 'Update' : 'Add'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ClinicalFindingsSection;
